import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Checkbox } from 'antd'
import { isEmpty } from 'ramda'
import $t from '../../../i18n'
import MaterialInput from '../../common/MaterialInput'
import Actions from '../../../actions'
import validateZipcode from '../../../utils/validateZipcode'
import { event, eventTypes } from '../../../consts/eventConsts'
import { formatClientSideError } from '../../../utils/eventUtils'
import Page from '../../common/Page/Page'
import PageCard from '../../common/PageCard'
import EditableItem from '../../common/EditableItem'
import { getFamilyPharmacies } from '../../../utils/familyUtils'
import { filterCanEditPersonalData } from '../../../utils/familyPermissionsUtils'

import './MyPharmacy.css'

const getValidationErrors = ({ zipcode }) => {
  const result = {}

  if (!zipcode) {
    result.zipcode = $t('errors.validation.missing')
  } else if (!validateZipcode(zipcode)) {
    result.zipcode = $t('errors.validation.invalidZipcode')
  }

  return (!isEmpty(result) && result) || null
}

const MyPharmacy = ({ onNext }) => {
  const dispatch = useDispatch()
  const userDetails = useSelector(state => state.profile.userDetails)
  const isLoadingSearchPharmacy = useSelector(
    state => state.ds.isLoadingSearchPharmacy
  )

  const pharmacies = useSelector(state => state.ds.pharmacies)
  const [zipcode, setZipcode] = useState()
  const [selectedPharmacy, setSelectedPharmacy] = useState(null)
  const [memberToEdit, setMemberToEdit] = useState(null)
  const [shouldValidate, setShouldValidate] = useState(false)
  const errors = shouldValidate && getValidationErrors({ zipcode })
  const activeMembers = useSelector(state => state.account?.activeMembers)
  const selectedMember = useSelector(state => state.account?.selectedMember)
  const familyPharmacies = getFamilyPharmacies(activeMembers, userDetails)
  const pharmaciesByMember = selectedMember?.id
    ? familyPharmacies?.filter(
        pharmacy =>
          pharmacy?.memberId.toString() === selectedMember?.id.toString()
      )
    : familyPharmacies
  const familyCanEdit = activeMembers?.some(member =>
    filterCanEditPersonalData(member)
  )
  // in case we have on next function, we are inside the wizard
  // we want to know that in order to render the emergency screen outside the pageCard
  const isInsideWizard = !!onNext
  const [inEditMode, setInEditMode] = useState(
    !!(isInsideWizard && !userDetails?.pharmacy)
  )
  const [inSearchMode, setInSearchMode] = useState(false)
  const anotherPatient = selectedMember ? selectedMember?.id : memberToEdit
  const [useForFamily, setUseForFamily] = useState(false)

  const getFamilyNames = () => {
    const names = []
    familyPharmacies?.forEach(member => {
      if (member?.memberId === userDetails?.id) {
        names.push($t('dialogs.myPharmacy.myself'))
      } else {
        var currMember = activeMembers.find(
          activeMember => activeMember?.id === member?.memberId
        )
        if (filterCanEditPersonalData(currMember)) {
          names.push(currMember?.firstName)
        }
      }
    })
    return names.join(', ')
  }

  const clearSelection = () => {
    setZipcode(null)
    setSelectedPharmacy(null)
    setInSearchMode(false)
    dispatch(Actions.createAction(Actions.SEARCH_PHARMACY_CLEARED))
  }

  const onEdit = memberId => {
    dispatch(
      Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
        type: eventTypes.click,
        eventName: event.clickEvent.click,
        data: memberId,
        element: 'Edit pharmacy details',
      })
    )
    setInEditMode(true)
    setMemberToEdit(memberId)
    clearSelection()
  }

  const onSearch = useCallback(() => {
    const validationErrors = getValidationErrors({ zipcode })

    if (validationErrors) {
      setShouldValidate(true)
      dispatch(
        Actions.createAction(Actions.ANALYTICS_EVENT_FIRED, {
          type: eventTypes.formEvent,
          eventName: event.formEvent.invalid,
          data: {
            zipcode,
          },
          element: 'Add pharmacy details',
          error: formatClientSideError(getValidationErrors({ zipcode })),
        })
      )
      return
    }
    setInSearchMode(true)
    dispatch(
      Actions.createAction(Actions.SEARCH_PHARMACY_REQUESTED, {
        zipcode,
      })
    )
  }, [zipcode])

  const onOk = () => {
    if (useForFamily) {
      familyPharmacies?.forEach(member => {
        var currMember = activeMembers?.find(
          activeMember => activeMember?.id === member?.memberId
        )
        // update only for members that can edit personal data or for the current user
        if (
          (currMember && filterCanEditPersonalData(currMember)) ||
          member?.memberId === userDetails?.id
        ) {
          dispatch(
            Actions.createAction(Actions.UPDATE_MY_PHARMACY_REQUESTED, {
              pharmacy: selectedPharmacy,
              patientId: member?.memberId,
            })
          )
        }
      })
    } else {
      dispatch(
        Actions.createAction(Actions.UPDATE_MY_PHARMACY_REQUESTED, {
          pharmacy: selectedPharmacy,
          patientId: anotherPatient ? anotherPatient : userDetails?.id,
        })
      )
    }
    setInSearchMode(false)
    setInEditMode(false)
    // on next comes from wizard component
    // we can use this component without the wizard and and 'onnext' will be undefined
    if (onNext) {
      onNext()
    }
  }

  const shouldHideDialogActions =
    (inSearchMode && !selectedPharmacy) || (!inSearchMode && !!selectedPharmacy)

  const PharmacyItem = ({ pharmacy }) => {
    if (!pharmacy) {
      return <div className="title"> {$t('dialogs.myPharmacy.noDetails')} </div>
    }
    const name = pharmacy?.StoreName
    const address = pharmacy
      ? `${pharmacy?.Address1} ${pharmacy?.Address2}, ${pharmacy?.City}, ${pharmacy?.State}, ${pharmacy?.ZipCode}`
      : ''
    return (
      <div className="pharmacy-details">
        <div className="title pharmacy-detail-title"> {name} </div>
        <div className="description">{address}</div>
      </div>
    )
  }

  const pageActions = [
    <Button
      key="1"
      className="page-action trackable"
      data-name="search-pharmacy-button"
      type="primary"
      loading={isLoadingSearchPharmacy}
      data-hidden={shouldHideDialogActions}
      onClick={onSearch}
    >
      {$t('dialogs.myPharmacy.search')}
    </Button>,
  ]

  const renderContent = () => {
    return (
      <div className="pharmacy-content">
        {!inSearchMode && !selectedPharmacy && (
          <>
            <div className="input-container">
              <MaterialInput
                className="zipCode-input"
                type="number"
                value={zipcode || ''}
                errorText={errors?.zipcode}
                onChange={e =>
                  e.target.value?.length <= 5 ? setZipcode(e.target.value) : {}
                }
                label={$t('dialogs.myPharmacy.zipCode')}
                max={99999}
                min={0}
              />
            </div>
            <div className="page-footer">{pageActions}</div>
          </>
        )}
        {inSearchMode && !isLoadingSearchPharmacy && (
          <>
            <div className="pharmacy-list-item">
              {pharmacies.map((pharmacy, i) => (
                <EditableItem
                  dataName={'pharmacy-item ' + pharmacy?.PharmacyId}
                  disableEdit={true}
                  showHeader={false}
                  key={i}
                  className={
                    selectedPharmacy?.PharmacyId === pharmacy?.PharmacyId
                      ? 'selected-editable'
                      : 'unselected-editable'
                  }
                  onClick={() => setSelectedPharmacy(pharmacy)}
                >
                  <PharmacyItem pharmacy={pharmacy} />
                </EditableItem>
              ))}
            </div>
            <div className="footer-pharmacy">
              {familyCanEdit && (
                <Checkbox
                  checked={useForFamily}
                  onChange={() => setUseForFamily(!useForFamily)}
                >
                  {$t('dialogs.myPharmacy.useForFamily', {
                    names: getFamilyNames(),
                  })}
                </Checkbox>
              )}
              <Button
                key="1"
                className="page-action trackable"
                data-name="save-pharmacy-button"
                disabled={!selectedPharmacy}
                type="primary"
                onClick={onOk}
              >
                {$t('common.save')}
              </Button>
            </div>
          </>
        )}
      </div>
    )
  }

  const handleOnBack = () => {
    if (inSearchMode) {
      clearSelection()
    } else if (!inSearchMode && inEditMode) {
      setInEditMode(false)
      setSelectedPharmacy(null)
    } else dispatch(Actions.createAction(Actions.NAVIGATED_BACK))
  }

  const getPharamcyPage = () => {
    return (
      <Page
        displayManageFamily={!inEditMode && !isInsideWizard}
        hideAlertBanner
        title={$t('dialogs.myPharmacy.title')}
        subtitle={$t('dialogs.myPharmacy.subtitle')}
        className="pharmacy-page"
      >
        {inEditMode && renderContent()}
        {!inEditMode &&
          pharmaciesByMember?.map((pharmacy, i) => (
            <div className="selected-pharmacy-wrapper" key={i}>
              <EditableItem
                dataName={'pharmacy-item ' + pharmacy?.pharmacy?.PharmacyId}
                onEdit={() => onEdit(pharmacy?.memberId)}
                disableEdit={!pharmacy?.canEdit}
                membersIds={[pharmacy?.memberId]}
                key={i}
              >
                <PharmacyItem pharmacy={pharmacy?.pharmacy} />
              </EditableItem>
            </div>
          ))}
      </Page>
    )
  }
  return isInsideWizard ? (
    getPharamcyPage()
  ) : (
    <PageCard onBack={handleOnBack} enableBack>
      {getPharamcyPage()}
    </PageCard>
  )
}

export default MyPharmacy
